'use client';

import { Crown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import type { ProjectPackState } from '@/types/project';

/**
 * Per-building pack-status pill, shared by the Packs storefront list
 * and the building page hero so the marking reads identically everywhere:
 *  - active pack → FILLED teal pill with the product NAME ("Facade Pack" /
 *    "Builder Pass" — brand terms, kept English in every locale);
 *  - no pack → muted grey outlined pill ("Free", the free tier).
 *
 * Presentational only. The activation affordance (tap target / button) is owned
 * by the caller, since the storefront binds the pack hero while the building
 * hero wraps the pill itself in a button that opens the pack paywall.
 */
export function BuildingPackTag({
  pack,
  className,
}: {
  pack?: ProjectPackState | null;
  className?: string;
}) {
  const tPacks = useTranslations('packs');
  const isActive = pack?.is_active === true;

  if (!isActive) {
    return (
      <span
        className={cn(
          'text-card-meta inline-flex shrink-0 items-center rounded-full border border-border/60 px-3 py-1 font-semibold text-muted-foreground',
          className
        )}
      >
        {tPacks('noPack')}
      </span>
    );
  }

  // Brand literal for the Builder slot; the purchased pack reuses the offer
  // title key (there is no static i18n key for the server-driven storefront
  // name). Mirrors Apple/Android.
  const label =
    pack?.source === 'builder' ? 'Builder Pass' : tPacks('packOfferTitle');

  return (
    <span
      className={cn(
        'text-card-meta text-primary-foreground inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-3 py-1 font-semibold',
        className
      )}
    >
      {/* Crown marks an active kit; the "No kit" branch above renders without it. */}
      <Crown className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}
